import { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { usePersistedState } from "@/hooks/usePersistedState";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { Droplet, Loader2, Gauge, DollarSign, Ruler } from "lucide-react";
import { toast } from "sonner";

// Visual ink volume + cost estimate for an artwork at a given size. Each estimate is logged for future training.
export const InkEstimateCard = ({ file = null, fileId = null, width, height, bleed = 0, unit = "in", quantity = 1, tab = "fitsize", auto = false }) => {
  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = usePersistedState("pns_studio_ink_machine", "");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const lastKeyRef = useRef("");

  useEffect(() => {
    api.get("/machines").then((r) => {
      const list = r.data || [];
      setMachines(list);
      setMachineId((cur) => cur || (list.find((m) => /mimaki/i.test(m.name))?.id) || (list[0]?.id) || "");
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estimate = async (silent = false) => {
    if (!machineId || !(parseFloat(width) > 0) || !(parseFloat(height) > 0) || (!file && !fileId)) {
      if (!silent) toast.error("Upload an image and set a valid size first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("machine_id", machineId);
      fd.append("width", String(parseFloat(width)));
      fd.append("height", String(parseFloat(height)));
      fd.append("bleed", String(parseFloat(bleed) || 0));
      fd.append("unit", unit);
      fd.append("quantity", String(quantity || 1));
      fd.append("source_tab", tab);
      if (fileId) fd.append("file_id", fileId); else fd.append("file", file);
      const { data } = await api.post("/studio/ink-estimate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      if (!silent) toast.success("Ink estimated & saved");
    } catch (e) { if (!silent) toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!auto || !machineId) return;
    if (!(parseFloat(width) > 0 && parseFloat(height) > 0) || (!file && !fileId)) return;
    const key = `${fileId || file?.name}|${width}|${height}|${unit}|${bleed}|${quantity}|${machineId}`;
    if (key === lastKeyRef.current) return;
    const t = setTimeout(() => { lastKeyRef.current = key; estimate(true); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, fileId, file, width, height, unit, bleed, quantity, machineId]);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`ink-card-${tab}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Droplet size={16} className="text-[#2495D3]" /> Ink estimate</div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-lg border px-2 text-sm bg-white max-w-[200px]" value={machineId} onChange={(e) => setMachineId(e.target.value)} data-testid={`ink-machine-${tab}`}>
            {machines.length === 0 && <option value="">No machines</option>}
            {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <Button size="sm" onClick={estimate} disabled={busy} data-testid={`ink-estimate-${tab}`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><Droplet size={15} className="mr-1.5" />Estimate</>}
          </Button>
        </div>
      </div>
      {res && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid={`ink-result-${tab}`}>
            <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1"><Gauge size={11} /> Coverage</div><div className="text-lg font-black">{res.coverage_pct}%</div></div>
            <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1"><Ruler size={11} /> Area</div><div className="text-lg font-black">{res.area_sqft} ft²</div></div>
            <div className="rounded-lg bg-white border border-slate-200 p-3"><div className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1"><Droplet size={11} /> Ink</div><div className="text-lg font-black">{res.ink_ml} ml</div></div>
            <div className="rounded-lg bg-[#2495D3]/10 border border-[#2495D3]/30 p-3"><div className="text-[10px] font-mono uppercase text-[#2495D3] flex items-center gap-1"><DollarSign size={11} /> Cost</div><div className="text-lg font-black text-[#2495D3]">{money(res.ink_cost)}</div></div>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">{res.machine_name} · {res.final_w_in}×{res.final_h_in}&quot; (incl. bleed) · saved to ink log for training.</p>
        </>
      )}
      {!res && <p className="text-[11px] text-slate-400 mt-2">Visual only — estimates ink volume &amp; cost from the image and size, and saves each result to train future pricing.</p>}
    </div>
  );
};

export default InkEstimateCard;
